import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from '@/contexts/AuthContext'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { LegacyPage } from '@/components/layout/LegacyPage'
import { LoginPage } from '@/pages/LoginPage'
import { AuthCallbackPage } from '@/pages/AuthCallbackPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { TrustSafetyHost } from '@/components/legacy/TrustSafetyHost'
import { CareersAdminHost } from '@/components/legacy/CareersAdminHost'
import { CareersPostingsPage } from '@/pages/CareersPostingsPage'
import { InfrastructurePage } from '@/pages/InfrastructurePage'
import { SettingsPage } from '@/pages/SettingsPage'
import { SupportPage } from '@/pages/SupportPage'
import { CommunitiesPage } from '@/pages/CommunitiesPage'
import { ChannelsPage } from '@/pages/ChannelsPage'
import { ApplicationsPage } from '@/pages/ApplicationsPage'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error && typeof error === 'object' && 'status' in error) {
          const status = Number(error.status)
          if (status === 401 || status === 403 || status === 404) return false
        }
        return failureCount < 2
      },
    },
  },
})

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />

            <Route element={<ProtectedRoute />}>
              <Route element={<AdminLayout />}>
                <Route index element={<DashboardPage />} />

                <Route
                  path="users"
                  element={
                    <LegacyPage
                      title="People"
                      description="Find an account, understand its history, and take an appropriate action."
                    >
                      <TrustSafetyHost tab="users" />
                    </LegacyPage>
                  }
                />

                <Route path="trust-safety" element={<Navigate to="/trust-safety/reports" replace />} />
                <Route
                  path="trust-safety/reports"
                  element={
                    <LegacyPage title="Reports" description="Review, investigate, and resolve user and content reports.">
                      <TrustSafetyHost tab="reports" />
                    </LegacyPage>
                  }
                />
                <Route
                  path="trust-safety/appeals"
                  element={
                    <LegacyPage title="Appeals & privacy" description="Account appeals and data deletion requests.">
                      <TrustSafetyHost tab="reports" appealFilter />
                    </LegacyPage>
                  }
                />
                <Route path="trust-safety/moderation" element={<Navigate to="/trust-safety/reports" replace />} />
                <Route
                  path="trust-safety/cases"
                  element={
                    <LegacyPage title="Cases" description="Merged duplicate reports grouped into investigation cases.">
                      <TrustSafetyHost tab="cases" />
                    </LegacyPage>
                  }
                />

                <Route
                  path="messages"
                  element={
                    <LegacyPage title="Reported messages" description="Review only the conversation snippets users intentionally disclosed in a report.">
                      <TrustSafetyHost tab="reports" targetFilter="message" />
                    </LegacyPage>
                  }
                />

                <Route path="careers" element={<Navigate to="/careers/postings" replace />} />
                <Route path="careers/postings" element={<CareersPostingsPage />} />
                <Route path="careers/applications" element={<ApplicationsPage />} />

                <Route
                  path="staff"
                  element={
                    <LegacyPage title="Staff access" description="Give people the minimum role and permissions they need.">
                      <CareersAdminHost tab="staff" />
                    </LegacyPage>
                  }
                />

                <Route
                  path="announcements"
                  element={
                    <LegacyPage title="Announcements" description="Broadcast system banners to chat, all pages, or staff-only.">
                      <TrustSafetyHost tab="announcements" />
                    </LegacyPage>
                  }
                />
                <Route
                  path="analytics"
                  element={
                    <LegacyPage title="Analytics" description="Platform growth, retention, and Trust & Safety metrics.">
                      <TrustSafetyHost tab="analytics" />
                    </LegacyPage>
                  }
                />
                <Route
                  path="audit"
                  element={
                    <LegacyPage title="Audit log" description="Searchable log of all administrative actions.">
                      <TrustSafetyHost tab="audit" />
                    </LegacyPage>
                  }
                />
                <Route
                  path="blog"
                  element={
                    <LegacyPage title="Blog" description="Write and publish posts on the public blog.">
                      <TrustSafetyHost tab="blog" />
                    </LegacyPage>
                  }
                />

                <Route path="communities" element={<CommunitiesPage />} />
                <Route path="channels" element={<ChannelsPage />} />
                <Route path="support" element={<SupportPage />} />
                <Route path="infrastructure" element={<InfrastructurePage />} />
                <Route path="voice" element={<InfrastructurePage variant="voice" />} />
                <Route path="settings" element={<SettingsPage />} />

                <Route path="roadmap" element={<SettingsPage initialTab="roadmap" />} />
                <Route path="themes" element={<SettingsPage initialTab="themes" />} />
                <Route path="safety" element={<SettingsPage initialTab="safety" />} />
                <Route path="status" element={<Navigate to="/infrastructure" replace />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
